import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { getAuth } from 'firebase/auth';

interface PermissionGuardProps {
  // Allow children to be a node or a render prop function
  children: React.ReactNode | ((hasPermission: boolean) => React.ReactNode);
  // Old prop name was 'permissions', change to 'requiredPermission' for clarity and consistency
  // If needing multiple, pass space-separated string as before
  requiredPermission: string;
  userId?: string; // Add the new optional userId prop
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({ children, requiredPermission, userId }) => {
  const { hasPermission } = usePermissions();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  
  // Don't render anything if not logged in
  if (!currentUser) {
    return null;
  }

  // If userId is provided and matches the current user's ID, grant access
  if (userId && currentUser.uid === userId) {
    return typeof children === 'function' ? <>{children(true)}</> : <>{children}</>;
  }

  const isAdmin = hasPermission('admin');

  // Admins bypass specific permission checks
  if (isAdmin) {
    // If children is a function, call it, otherwise render directly
    return typeof children === 'function' ? <>{children(true)}</> : <>{children}</>;
  }

  // Parse the space-separated permissions string into an array
  const requiredPermissionsArray = requiredPermission.split(' ').filter(Boolean);

  // Check if the user has at least one of the required permissions
  const hasRequiredPermissionCheck = requiredPermissionsArray.some(permission => 
    hasPermission(permission)
  );

  if (hasRequiredPermissionCheck) {
    // If children is a function, call it, otherwise render directly
    return typeof children === 'function' ? <>{children(true)}</> : <>{children}</>;
  }

  // If none of the conditions are met, render the result of the function call with false, or null if not a function
  return typeof children === 'function' ? <>{children(false)}</> : null;
}; 